/* Scira Diagnostics: the first tenant, and the production configuration.
   Behaviour identical to before the tenant layer existed; this only names what was implicit. */
import { ALL_PATHWAYS } from '../data/atlas';
/* The market model now comes from the database export rather than the hand-curated seeds.
   Those seeds fed the original 38 countries; the export carries all 78, including the twelve
   European markets that previously held waiting lists and no activity figures at all. */
import { MARKET_COUNTRIES, MARKET_SITES } from '../data/atlas.markets';
/* The United States laboratory layer used to be imported here and concatenated, because it had
   been loaded into Postgres but the seed was rebuilt by hand and never picked it up. Now that
   research/export_atlas_seed.py does the export it claims to do, all 138 arrive in MARKET_SITES,
   and concatenating the file as well would list every one of them twice. */
import { OPPORTUNITIES, PARTNERS, TENDERS } from '../data/commercial';
import { ASSETS } from '../data/studio';
import { WAITING_CAVEATS, COUNTS_PER_ORGAN_NEEDED } from '../data/atlas.waiting';
import { GRAFT_YEARS } from '../data/atlas.core';
import { PUBLISHED_PREVALENCE } from '../data/atlas.prevalence';
/* Scira's own contracts and receivables. They were inline in Operations.tsx and
   Ledger.tsx, which put them in every build; see the note in that file. */
import { SCIRA_CONTRACTS, SCIRA_INVOICES } from '../data/scira.examples';
import { demo } from '../data/demo';
import type { Tenant } from './types';

export const TENANT: Tenant = {
  key: 'scira',
  brand: {
    productName: 'Ariadne Forum',
    orgName: 'Scira Diagnostics',
    ink: '#0C1B2A', accent: '#1B9AA8', paper: '#F6F4EF',
    inkDark: '#F3F6F8', paperDark: '#0A1420',
  },
  vocabulary: {
    site: 'Centre', sitePlural: 'Centres',
    atlas: 'Atlas',
    atlasSubtitle:
      'The transplant market model. Volumes, laboratories, reimbursement routes and clinical '
      + 'leadership, with the provenance of every figure recorded alongside it.',
    statement:
      'The transplant world, mapped. Every centre, every laboratory, every route to '
      + 'reimbursement, in one place.',
    volumeLabel: 'Transplants / year',
    population: 'Prevalent grafts',
    /* Deceased against living donation. A donation concept, so it belongs to this tenant. */
    volumeSubMetric: 'Living donor',
    populationFoot: 'Surveillance base, derived',
    segment: { label: 'All organs', singular: 'organ',
               keys: ['kidney', 'liver', 'heart', 'lung', 'pancreas', 'hsct_allo'],
               /* Scira's own words for its own segments. These were previously read from a map
                  in shared code, which is why a tenant segmenting by anything else got a blank
                  filter. */
               labels: {
                 kidney: 'Kidney', liver: 'Liver', heart: 'Heart', lung: 'Lung',
                 pancreas: 'Pancreas', intestine: 'Intestine',
                 kidney_pancreas: 'Kidney-pancreas', heart_lung: 'Heart-lung',
                 cardiothoracic: 'Cardiothoracic (combined)',
                 hsct_allo: 'HSCT (allo)', hsct_auto: 'HSCT (auto)',
               } },
    countryFacts: [
      { label: 'Donors per million', key: 'donorsPmp' },
      { label: 'Donation model', key: 'donationModel' },
      { label: 'Market priority', key: 'priority' },
      { label: 'Entry mode', key: 'entryMode' },
      { label: 'Registry', key: 'registry' },
    ],
    /* Brought back from the demonstration tenant, where building it made obvious that the
       headline tile is not the addressable market. For a surveillance diagnostic the same
       narrowing applies and is arguably sharper: a prevalent graft is only addressable while
       the patient is in active follow-up at a centre that can order the test. */
    funnel: {
      title: 'From prevalent grafts to tested patients',
      note:
        'The prevalent graft tile is the surveillance base, not the market. A graft is only '
        + 'addressable while the recipient is in active follow-up somewhere that can order and '
        + 'be paid for a molecular test. The steps below narrow it, and the modelled ones say '
        + 'so.',
      steps: [
        { label: 'Functioning kidney grafts, United States', value: 273_041,
          basis: 'Published national count, end-2024',
          source: 'USRDS. This is the one number in the surveillance model with a real '
                + 'published anchor behind it, and the 8.44 graft-years multiplier used '
                + 'elsewhere in the Atlas is calibrated to reproduce it.' },
        { label: 'In active transplant-centre follow-up', value: 218_000,
          basis: 'Modelled at 80% of prevalent grafts',
          source: 'MODELLED, not published. Recipients transfer to community nephrology over '
                + 'time and are then reached through a different channel, if at all. The 80% '
                + 'figure is an assumption and should be replaced with registry follow-up data '
                + 'where a country publishes it.' },
        { label: 'Addressable for molecular surveillance', value: 152_000,
          basis: 'Modelled: in follow-up, beyond the early post-operative period, '
               + 'at a centre with a reimbursement route',
          source: 'MODELLED. Depends entirely on the reimbursement layer: in a market with no '
                + 'funded route the addressable number is zero however many grafts exist. This '
                + 'is why the pathway model and not the graft count drives sequencing.' },
        { label: 'Currently under molecular surveillance', value: null,
          basis: 'Not established',
          source: 'GAP. No independent published figure for dd-cfDNA surveillance penetration '
                + 'that we have verified. Vendor-reported test volumes are not a substitute '
                + 'and are not used here. This row stays empty until it can be sourced.' },
      ],
    },
    /* Exactly what the hardcoded pills and the "Laboratory" column used to render, now stated
       by the tenant that actually means them. '@site' is substituted with the tenant's own word
       for a site, so this pill reads "Centre" here and "Hospital" elsewhere. */
    buyingRoles: { demand: 'Demand: the transplant centre', buying: 'Buying: usually the H&I laboratory',
                   funding: 'Funding: commissioner or payer', process: 'Process: procurement body' },
    defaultDealName: 'dd-cfDNA surveillance',
    siteKinds: [
      { key: 'transplant_centre', label: '@site', pill: 'mute' as const },
      { key: 'hi_lab', label: 'H&I laboratory', pill: 'ok' as const,
        column: true, technical: true },
      { key: 'reference_lab', label: 'Reference lab', pill: 'accent' as const, technical: true },
    ],

    /* Circus's product categories: the diagnostics vocabulary exactly as it stood when it
       lived in shared code, moved here because it is Scira's, not the product's. */
    offeringCategories: [
      { key: 'ddcfdna', label: 'Donor-derived cfDNA' },
      { key: 'hla_typing', label: 'HLA typing' },
      { key: 'antibody', label: 'Antibody and crossmatch' },
      { key: 'chimerism', label: 'Chimerism' },
      { key: 'biopsy_molecular', label: 'Molecular biopsy' },
      { key: 'platform', label: 'Instruments and platforms' },
      { key: 'other', label: 'Other' },
    ],
    notMapped:
      'China, India, Japan, South Korea and most of Asia and Africa. China in particular runs '
      + 'one of the largest transplant programmes in the world and is deliberately excluded '
      + 'rather than estimated: activity figures are not independently verifiable to the '
      + 'standard used everywhere else here, and a number that cannot be checked is worse on '
      + 'this map than a stated absence.',
  },

  demo: false,
  usesBuiltInExamples: true,
  /* How an approved trip gets booked. TravelPerk is the primary channel: a handoff out by
     deep link, because its public API deliberately has no booking-creation endpoint, with
     the sync back scaffolded and dormant until a Pro subscription and OAuth credentials
     exist (lib/travelperk.ts holds the design and the activation steps). "Book direct"
     stays for the trip TravelPerk cannot carry. */
  travel: {
    bookingChannels: [
      { key: 'travelperk', label: 'TravelPerk', provider: 'travelperk',
        instructions: 'Book the approved trip in the TravelPerk app. Paste the copied '
          + 'summary into the trip name or notes so the confirmation matches back to this '
          + 'request, and attach the confirmation here when it arrives.' },
      { key: 'direct', label: 'Book direct',
        instructions: 'Book it yourself with the carrier or hotel, within policy, and '
          + 'attach the confirmation to this request.' },
    ],
    /* Tenant DATA, not product. The founder's independent travel agency offers staff
       agent-discounted rates on PERSONAL trips. It is a perk, not a booking channel:
       company travel goes through the channels above, and nothing about this arrangement
       exists in shared code. The isolation checker holds these strings against every
       other tenant's bundle. */
    perk: {
      title: 'Discounted personal travel',
      text: 'Personal trips booked through Astonbury Travel, the founder’s independent '
        + 'travel agency, attract agent-discounted rates for staff and family. Personal '
        + 'travel only: company trips go through the booking channels on your requests.',
      url: 'https://astonburytravel.co.uk',
      linkLabel: 'Astonbury Travel',
    },
  },
  atlas: {
    caveats: WAITING_CAVEATS, countsPerOrganNeeded: COUNTS_PER_ORGAN_NEEDED,
    prevalenceMultipliers: GRAFT_YEARS,
    /* Four registries publish a functioning-graft count. Those countries use it;
       everyone else is modelled. See data/atlas.prevalence.ts for why one
       multiplier cannot serve all 78. */
    publishedPrevalence: PUBLISHED_PREVALENCE,
    /* The four organs with published national activity and waiting figures. Stated here rather
       than assumed in Atlas, which is what let a second tenant have a different set. */
    nationalSegments: ['kidney', 'liver', 'heart', 'lung'],
    /* An SPK produces a functioning kidney graft and a pancreas graft, and both parts belong to
       their own segment in full. NHSBT reports heart and lung as one cardiothoracic figure, so
       each takes half. Behaviour identical to the branch this replaced in Atlas. */
    combinedSplits: {
      kidney:   [{ from: 'kidney_pancreas', share: 1 }],
      pancreas: [{ from: 'kidney_pancreas', share: 1 }],
      heart:    [{ from: 'cardiothoracic',  share: 0.5 }],
      lung:     [{ from: 'cardiothoracic',  share: 0.5 }],
    },
  },
  seed: {
    site: MARKET_SITES,
    country: MARKET_COUNTRIES.map((c) => ({ ...c, id: c.iso3 })),
    pathway: ALL_PATHWAYS,
    opportunity: OPPORTUNITIES,
    partner: PARTNERS,
    tender: TENDERS,
    asset: ASSETS,
    /* Contracts and receivables now come from the tenant rather than from inline literals in
       the shared modules, so they are in Scira's bundle and nobody else's. The remainder are
       still inline behind the demo() gate and should follow the same way. */
    /* STILL BEHIND demo(), and this was briefly lost.

       These are worked examples: a consultancy agreement and a handful of receivables written
       so the modules could be evaluated before Postgres existed. demo() returns nothing once a
       backend is configured, because the moment real records exist an invented contract stops
       illustrating and starts being a lie on a screen someone might act on.

       Moving them out of Operations.tsx and Ledger.tsx to stop them shipping in other tenants'
       bundles accidentally dropped that gate, and they reappeared in Scira's live Forum. The
       gate belongs here now: build-time tenancy keeps them out of other bundles, and demo()
       keeps them off a screen that has real data behind it. Both are needed and they do
       different jobs. */
    invoice: demo(SCIRA_INVOICES), contract: demo(SCIRA_CONTRACTS),
    family: [], deadline: [], crew: [], funder: [], call: [],
    qualityDoc: [], nonconformance: [], capa: [], notice: [], channel: [],
    dataDue: [], reviewQueue: [],
    competitor: [], offering: [], competitorEvent: [],
    strategy: [], move: [], suggestion: [],
  },
};
